import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import { PrivatePageLayout, PageHeader } from "../../components/PrivatePage";
import SelectSymbol from "../../components/SelectSymbol/SelectSymbol";
import SwitchInput from "../../components/SwitchInput/SwitchInput";
import SpotFuturesToggle from "../../components/SpotFuturesToggle/SpotFuturesToggle";
import { AppButton, AppButtonLink } from "../../components/AppButton/AppButton";
import MonitorIndex from "../Monitors/MonitorModal/MonitorIndex";
import {
    SetupField,
    SetupSection,
    OrderTemplateColumn,
    BotLogicColumn,
} from "./UnifiedSetupSections";
import { saveMonitor, getMonitors, getMonitor, getMonitorsBySymbol } from "../../services/MonitorsService";
import { getSymbol } from "../../services/SymbolsService";
import { getDefaultIndexes } from "../../services/BeholderService";
import { saveOrderTemplate, getOrderTemplate, getAllOrderTemplates } from "../../services/OrderTemplatesService";
import { saveAutomation as saveAutomationApi, getAutomation } from "../../services/AutomationsService";
import { saveStrategy, getStrategy } from "../../services/StrategiesService";
import { createAlgoBot } from "../../services/BotsService";
import {
    notifySetupError,
    notifySetupSuccess,
    notifySetupValidation,
    runSetupStep,
} from "./setupNotifications";
import {
    buildAutomationIndexesForSymbol,
    filterMonitorsBySymbol,
    filterOrderTemplatesForPair,
    formatMonitorOptionLabel,
} from "./automationSetupUtils";
import "./UnifiedSetup.css";

const DEFAULT_ORDER = {
    name: "",
    symbol: "",
    type: "MARKET",
    side: "BUY",
    limitPrice: "",
    limitPriceMultiplier: 1,
    stopPrice: "",
    stopPriceMultiplier: 1,
    quantity: "",
    quantityMultiplier: 1,
    marginType: null,
    reduceOnly: false,
    leverage: null,
};

function createDefaultAutomation(name) {
    return {
        name,
        symbol: "",
        conditions: "",
        actions: [],
        schedule: "",
        monitorId: null,
        orderTemplateId: null,
        maxOpenPositions: 0,
        isActive: true,
        logs: false,
    };
}

function cloneActions(actions) {
    return Array.isArray(actions) ? actions.map((action) => ({ ...action })) : [];
}

function UnifiedSetup() {
    const history = useHistory();
    const location = useLocation();
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);

    const [market, setMarket] = useState("SPOT");
    const [setupSymbol, setSetupSymbol] = useState("BTCUSDT");

    const [buyOrderId, setBuyOrderId] = useState(null);
    const [sellOrderId, setSellOrderId] = useState(null);
    const [buyAutomationId, setBuyAutomationId] = useState(null);
    const [sellAutomationId, setSellAutomationId] = useState(null);
    const [buyMaxOpenPositions, setBuyMaxOpenPositions] = useState(0);

    const [createdMonitorId, setCreatedMonitorId] = useState(null);
    const [createdStrategyId, setCreatedStrategyId] = useState(null);

    const [automationIndexes, setAutomationIndexes] = useState([]);
    const [symbolMeta, setSymbolMeta] = useState({});
    const [symbolMonitors, setSymbolMonitors] = useState([]);
    const [symbolOrderTemplates, setSymbolOrderTemplates] = useState([]);
    const [existingMonitors, setExistingMonitors] = useState([]);


    const [buyOrderData, setBuyOrderData] = useState({ ...DEFAULT_ORDER, side: "BUY" });
    const [sellOrderData, setSellOrderData] = useState({ ...DEFAULT_ORDER, side: "SELL" });
    const [buyAutomationData, setBuyAutomationData] = useState(() => createDefaultAutomation("Buy logic"));
    const [sellAutomationData, setSellAutomationData] = useState(() => createDefaultAutomation("Sell logic"));

    const generateUniqueName = () => `Monitor_${Date.now()}_${Math.floor(Math.random() * 10000)}`;

    const [monitorData, setMonitorData] = useState(() => ({
        name: generateUniqueName(),
        symbol: "BTCUSDT",
        type: "CANDLES",
        interval: "1m",
        indexes: "",
        isActive: true,
        logs: false,
    }));

    const [strategyData, setStrategyData] = useState({
        name: "My algo bot",
        symbol: "BTCUSDT",
        shareMode: "private",
        sharedEmails: "",
        isActive: true,
    });

    const fetchExistingItems = async () => {
        try {
            const monitorsRes = await getMonitors(1);
            setExistingMonitors(monitorsRes.rows || []);

            if (setupSymbol) {
                const orders = await getAllOrderTemplates(setupSymbol);
                setSymbolOrderTemplates(Array.isArray(orders) ? orders : []);
            }
        } catch (err) {
            console.error("Failed to load existing items:", err);
        }
    };

    useEffect(() => {
        fetchExistingItems();
    }, []);

    const syncSymbol = useCallback((symbol) => {
        const nextSymbol = (symbol || "").trim().toUpperCase();
        setSetupSymbol(nextSymbol);
        setMonitorData((prev) => ({ ...prev, symbol: nextSymbol }));
        setStrategyData((prev) => ({ ...prev, symbol: nextSymbol }));
        if (!buyOrderId) setBuyOrderData((prev) => ({ ...prev, symbol: nextSymbol }));
        if (!sellOrderId) setSellOrderData((prev) => ({ ...prev, symbol: nextSymbol }));
        if (!buyAutomationId) setBuyAutomationData((prev) => ({ ...prev, symbol: nextSymbol }));
        if (!sellAutomationId) setSellAutomationData((prev) => ({ ...prev, symbol: nextSymbol }));
    }, [buyAutomationId, buyOrderId, sellAutomationId, sellOrderId]);

    const clearLogicConditions = useCallback(() => {
        setBuyAutomationData((prev) => ({ ...prev, conditions: "", indexes: "" }));
        setSellAutomationData((prev) => ({ ...prev, conditions: "", indexes: "" }));
    }, []);

    function logicPayload(logic) {
        if (!logic) return null;
        return {
            ...logic,
            actions: cloneActions(logic.actions || []),
        };
    }

    const resetDependentsForSymbolChange = useCallback((nextSymbol) => {
        const normalized = (nextSymbol || "").trim().toUpperCase();

        if (createdMonitorId) {
            const selected = [...symbolMonitors, ...existingMonitors].find(
                (monitor) => String(monitor.id) === String(createdMonitorId),
            );
            if (selected && selected.symbol !== normalized && selected.symbol !== "*") {
                setMonitorData({
                    name: generateUniqueName(),
                    symbol: normalized,
                    type: "CANDLES",
                    interval: "1m",
                    indexes: "",
                    isActive: true,
                    logs: false,
                });
                setCreatedMonitorId(null);
            }
        }

        clearLogicConditions();

        if (buyOrderId) {
            const orderSymbol = (buyOrderData.symbol || "").trim().toUpperCase();
            if (orderSymbol && orderSymbol !== normalized) {
                setBuyOrderId(null);
                setBuyOrderData({ ...DEFAULT_ORDER, side: "BUY", symbol: normalized, name: "" });
            }
        }

        if (sellOrderId) {
            const orderSymbol = (sellOrderData.symbol || "").trim().toUpperCase();
            if (orderSymbol && orderSymbol !== normalized) {
                setSellOrderId(null);
                setSellOrderData({ ...DEFAULT_ORDER, side: "SELL", symbol: normalized, name: "" });
            }
        }
    }, [
        buyOrderData.symbol,
        buyOrderId,
        clearLogicConditions,
        createdMonitorId,
        existingMonitors,
        sellOrderData.symbol,
        sellOrderId,
        symbolMonitors,
    ]);

    const handleTradingPairChange = useCallback((event) => {
        const nextSymbol = (event.target.value || "").trim().toUpperCase();
        if (!nextSymbol || nextSymbol === setupSymbol) return;
        resetDependentsForSymbolChange(nextSymbol);
        syncSymbol(nextSymbol);
    }, [resetDependentsForSymbolChange, setupSymbol, syncSymbol]);

    const handleMarketChange = useCallback((nextMarket) => {
        const normalized = nextMarket === "futures" ? "FUTURES" : "SPOT";
        if (normalized === market) return;
        setMarket(normalized);
        clearLogicConditions();

        if (buyOrderId) {
            setBuyOrderId(null);
            setBuyOrderData({ ...DEFAULT_ORDER, side: "BUY", symbol: setupSymbol, name: "" });
        }
        if (sellOrderId) {
            setSellOrderId(null);
            setSellOrderData({ ...DEFAULT_ORDER, side: "SELL", symbol: setupSymbol, name: "" });
        }
    }, [buyOrderId, clearLogicConditions, market, sellOrderId, setupSymbol]);

    function getSharedWith(shareMode, sharedEmails) {
        if (shareMode === "public") return "everyone";
        if (shareMode === "shared") return (sharedEmails || "").trim() || "none";
        return "none";
    }

    function logicIsConfigured(logic) {
        const name = (logic?.name || "").trim();
        const conditions = (logic?.conditions || "").trim();
        return !!name && !!conditions;
    }

    function buildAlgoBotPayload() {
        return {
            name: strategyData.name,
            symbol: setupSymbol,
            market,
            shareMode: strategyData.shareMode,
            sharedEmails: strategyData.sharedEmails,
            isActive: strategyData.isActive !== false,
            monitorId: createdMonitorId || null,
            monitor: createdMonitorId ? null : monitorData,
            buyOrderId: buyOrderId || null,
            sellOrderId: sellOrderId || null,
            buyOrder: buyOrderId ? null : buyOrderData,
            sellOrder: sellOrderId ? null : sellOrderData,
            buyLogic: logicIsConfigured(buyAutomationData) ? logicPayload(buyAutomationData) : null,
            sellLogic: logicIsConfigured(sellAutomationData) ? logicPayload(sellAutomationData) : null,
            buyMaxOpenPositions: buyMaxOpenPositions,
        };
    }

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("strategyId") && !params.get("botId")) {
            params.set("botId", params.get("strategyId"));
            params.delete("strategyId");
            history.replace(`/unified-setup?${params.toString()}`);
            return;
        }
    }, [history, location.search]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const strategyId = params.get("botId");
        if (!strategyId) return;

        getStrategy(strategyId)
            .then(async (strategy) => {
                if (!strategy) return;

                const symbol = strategy.symbol || "BTCUSDT";
                syncSymbol(symbol);

                setCreatedStrategyId(String(strategy.id));
                setStrategyData({
                    name: strategy.name || "My algo bot",
                    symbol,
                    shareMode: strategy.sharedWith === "everyone"
                        ? "public"
                        : (strategy.sharedWith && !["everyone", "none"].includes(strategy.sharedWith) ? "shared" : "private"),
                    sharedEmails: strategy.sharedWith && !["everyone", "none"].includes(strategy.sharedWith)
                        ? strategy.sharedWith
                        : "",
                    isActive: strategy.isActive !== false,
                });

                if (strategy.monitorId) {
                    const monitor = strategy.monitor || await getMonitor(strategy.monitorId);
                    if (monitor) {
                        setMonitorData({
                            name: (monitor.name || '').trim() || generateUniqueName(),
                            symbol: monitor.symbol || symbol,
                            type: monitor.type || "CANDLES",
                            interval: monitor.interval || "1m",
                            indexes: monitor.indexes || "",
                            isActive: monitor.isActive !== false,
                            logs: monitor.logs || false,
                        });
                        setCreatedMonitorId(String(strategy.monitorId));
                    }
                }

                const hydrateOrderFromAutomation = async (automation, side) => {
                    if (!automation?.actions?.length) return;
                    const orderAction = automation.actions.find((action) => action.orderTemplateId);
                    if (!orderAction?.orderTemplateId) return;
                    const orderTemplate = orderAction.orderTemplate
                        || await getOrderTemplate(orderAction.orderTemplateId);
                    if (!orderTemplate) return;
                    if (side === "buy") {
                        setBuyOrderId(orderTemplate.id);
                        setBuyOrderData({ ...orderTemplate, leverage: orderTemplate.leverage || null });
                    } else {
                        setSellOrderId(orderTemplate.id);
                        setSellOrderData({ ...orderTemplate, leverage: orderTemplate.leverage || null });
                    }
                };

                const hydrateAutomation = async (automationId, embedded, side) => {
                    if (!automationId) return;
                    const automation = embedded || await getAutomation(automationId);
                    if (!automation) return;

                    const logicState = {
                        ...automation,
                        actions: cloneActions(automation.actions || []),
                        maxOpenPositions: automation.maxOpenPositions || 0,
                    };

                    if (side === "buy") {
                        setBuyAutomationId(automation.id);
                        setBuyAutomationData(logicState);
                        setBuyMaxOpenPositions(automation.maxOpenPositions || 0);
                    } else {
                        setSellAutomationId(automation.id);
                        setSellAutomationData(logicState);
                    }

                    await hydrateOrderFromAutomation(automation, side);
                };

                await hydrateAutomation(strategy.buyAutomationId, strategy.buyAutomation, "buy");
                if (strategy.sellAutomationId !== strategy.buyAutomationId) {
                    await hydrateAutomation(strategy.sellAutomationId, strategy.sellAutomation, "sell");
                }
            })
            .catch((err) => {
                console.error("Failed to load strategy for edit:", err);
                setError(err.response?.data || err.message || "Failed to load strategy.");
            });
    }, [location.search, syncSymbol]);

    const updateMonitor = (fieldOrEvent, value) => {
        if (fieldOrEvent && fieldOrEvent.target) {
            const { id, value: val } = fieldOrEvent.target;
            setMonitorData((prev) => ({ ...prev, [id]: (val || "").trim() }));
        } else {
            setMonitorData((prev) => ({ ...prev, [fieldOrEvent]: value }));
        }
    };

    const updateStrategy = (field, value) => {
        setStrategyData((prev) => ({ ...prev, [field]: value }));
    };

    async function persistSharing(shareMode, sharedEmails) {
        if (!createdStrategyId) return;
        try {
            await saveStrategy(createdStrategyId, {
                shareMode,
                sharedEmails,
                sharedWith: getSharedWith(shareMode, sharedEmails),
            });
        } catch (err) {
            console.error("Failed to update sharing:", err);
            setError(err.response?.data || err.message || "Failed to update sharing.");
        }
    }

    function handleShareModeChange(event) {
        const shareMode = event.target.value;
        updateStrategy("shareMode", shareMode);
        persistSharing(shareMode, strategyData.sharedEmails);
    }

    function handleSharedEmailsChange(event) {
        const sharedEmails = event.target.value;
        updateStrategy("sharedEmails", sharedEmails);
    }

    function handleSharedEmailsBlur(event) {
        if (!createdStrategyId || strategyData.shareMode !== "shared") return;
        persistSharing(strategyData.shareMode, event.target.value);
    }

    function makeOrderChangeHandler(setter) {
        return (event) => {
            const { id, value, type, checked } = event.target || event;
            const newValue = type === "checkbox" ? checked : value;
            setter((prev) => ({ ...prev, [id]: newValue }));
        };
    }

    function makeAutomationChangeHandler(setter) {
        return (fieldOrEvent, value) => {
            if (!fieldOrEvent) return;
            if (typeof fieldOrEvent === "string") {
                setter((prev) => ({ ...prev, [fieldOrEvent]: value }));
            } else if (fieldOrEvent.target) {
                const { id, value: val, type, checked } = fieldOrEvent.target;
                setter((prev) => ({ ...prev, [id]: type === "checkbox" ? checked : val }));
            } else if (typeof fieldOrEvent === "object") {
                setter((prev) => ({ ...prev, ...fieldOrEvent }));
            }
        };
    }

    const handleSelectExistingMonitor = (e) => {
        const id = e.target.value;
        if (!id) {
            setMonitorData({
                name: generateUniqueName(),
                symbol: setupSymbol,
                type: "CANDLES",
                interval: "1m",
                indexes: "",
                isActive: true,
                logs: false,
            });
            setCreatedMonitorId(null);
            return;
        }

        const existing = [...symbolMonitors, ...existingMonitors].find((monitor) => String(monitor.id) === id);
        if (existing) {
            setMonitorData({
                name: (existing.name || '').trim() || generateUniqueName(),
                symbol: existing.symbol || setupSymbol,
                type: existing.type || "CANDLES",
                interval: existing.interval || "1m",
                indexes: existing.indexes || "",
                isActive: existing.isActive !== false,
                logs: existing.logs || false,
            });
            setCreatedMonitorId(id);
            syncSymbol(existing.symbol || setupSymbol);
        }
    };

    const getMonitorIdFromConditions = (conditions) => {
        if (!conditions || typeof conditions !== "string") return null;
        const matches = conditions.match(/MEMORY\s*\[\s*['"]([^'"]+)['"]\s*\]/g);
        if (!matches || matches.length === 0) return null;

        for (const match of matches) {
            const nameMatch = match.match(/MEMORY\s*\[\s*['"]([^'"]+)['"]\s*\]/);
            if (nameMatch && nameMatch[1]) {
                const monitorName = nameMatch[1];
                const found = automationIndexes.find((idx) => idx.variable === monitorName && idx.monitorId);
                if (found?.monitorId) return found.monitorId;
            }
        }
        return null;
    };

    const saveOrder = async (type) => {
        const orderData = type === "buy" ? buyOrderData : sellOrderData;
        if (!orderData.name) {
            throw new Error(`${type === "buy" ? "Buy" : "Sell"} order template name is required.`);
        }

        const payload = {
            ...orderData,
            symbol: setupSymbol,
            side: type === "buy" ? "BUY" : "SELL",
            leverage: market === "FUTURES" ? orderData.leverage : null,
        };

        const res = await saveOrderTemplate(null, payload);
        const id = res?.id || res?.data?.id;
        if (!id) throw new Error("Order template was not created. No ID returned from server.");
        return id;
    };

    const persistAutomation = async (type, orderId, monitorId, automationId = null) => {
        const automationData = type === "buy" ? buyAutomationData : sellAutomationData;
        const label = type === "buy" ? "Buy logic" : "Sell logic";
        if (!automationData.name) {
            throw new Error(`${label} name is required.`);
        }
        if (!automationData.conditions) {
            throw new Error(`${label} needs at least one condition.`);
        }
        const actions = automationData.actions || [];
        if (!actions.length) {
            throw new Error(`${label} needs at least one action.`);
        }
        if (!orderId) {
            throw new Error(`${label} requires a linked order template.`);
        }

        const detectedMonitorId = getMonitorIdFromConditions(automationData.conditions || "");
        const autoToSave = {
            name: automationData.name,
            symbol: setupSymbol,
            conditions: automationData.conditions || "",
            actions: cloneActions(actions),
            schedule: automationData.schedule || null,
            indexes: automationData.indexes || "",
            orderTemplateId: orderId,
            maxOpenPositions: type === "buy" ? buyMaxOpenPositions : 0,
            isActive: automationData.isActive !== false,
            logs: automationData.logs || false,
            monitorId: monitorId || detectedMonitorId || null,
        };

        const res = await saveAutomationApi(automationId, autoToSave);
        const id = res?.id || res?.data?.id || automationId;
        if (!id) throw new Error(`${label} was not saved. No ID returned from server.`);
        return id;
    };

    const saveMonitorIfNeeded = async () => {
        const payload = { ...monitorData, symbol: setupSymbol };

        if (createdMonitorId) {
            await saveMonitor(createdMonitorId, payload);
            return createdMonitorId;
        }
        try {
            const res = await saveMonitor(null, payload);
            const newId = res?.id || res?.data?.id;
            if (!newId) throw new Error("Monitor was not created. No ID returned from server.");
            setCreatedMonitorId(newId);
            return newId;
        } catch (e) {
            if (e.response?.status === 409) {
                const fresh = { ...payload, name: generateUniqueName() };
                const res = await saveMonitor(null, fresh);
                const newId = res?.id || res?.data?.id;
                if (!newId) throw new Error("Monitor was not created after retry. No ID returned from server.");
                setCreatedMonitorId(newId);
                return newId;
            }
            throw e;
        }
    };

    const validateSetup = () => {
        const missing = [];

        const indexesValue = String(monitorData.indexes || "").trim();
        if (!createdMonitorId && (!monitorData.name || !indexesValue)) {
            missing.push("monitor signal (name and technical index)");
        }

        const hasBuyOrder = buyOrderId || buyOrderData.name;
        const hasSellOrder = sellOrderId || sellOrderData.name;
        if (!hasBuyOrder && !hasSellOrder) {
            missing.push("at least one buy or sell order template");
        }

        const buyConfigured = logicIsConfigured(buyAutomationData);
        const sellConfigured = logicIsConfigured(sellAutomationData);

        if (!buyConfigured && !sellConfigured) {
            missing.push("at least one buy or sell logic with conditions");
        }

        if (buyConfigured && !(buyAutomationData.actions || []).length) {
            missing.push("at least one buy logic action");
        }

        if (sellConfigured && !(sellAutomationData.actions || []).length) {
            missing.push("at least one sell logic action");
        }

        if (!strategyData.name) missing.push("bot name");
        if (!setupSymbol) missing.push("trading pair");

        if (missing.length === 0) return "";
        return `Cannot save bot. Missing: ${missing.join("; ")}.`;
    };

    const handleCreateBot = async () => {
        const validationError = validateSetup();
        if (validationError) {
            setError(validationError);
            notifySetupValidation(validationError);
            return;
        }

        setIsSaving(true);
        setError("");
        setMessage("");
        setShowSuccess(false);

        try {
            const result = await createAlgoBot(buildAlgoBotPayload());
            const nextStrategyId = result?.sourceId || result?.strategy?.id;
            if (nextStrategyId) setCreatedStrategyId(String(nextStrategyId));

            const successText = notifySetupSuccess({
                botName: strategyData.name,
                symbol: setupSymbol,
            });

            setShowSuccess(true);
            setMessage(successText);
            await fetchExistingItems();
        } catch (err) {
            console.error(err);
            const text = notifySetupError("Bot creation", err);
            setError(text);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveBot = async () => {
        const validationError = validateSetup();
        if (validationError) {
            setError(validationError);
            notifySetupValidation(validationError);
            return;
        }

        if (!createdStrategyId) {
            return handleCreateBot();
        }

        setIsSaving(true);
        setError("");
        setMessage("");

        try {
            const monitorId = await runSetupStep("Market signal", () => saveMonitorIfNeeded());

            if (!buyOrderId && buyOrderData.name) {
                const newBuyOrderId = await runSetupStep("Buy order template", () => saveOrder("buy"));
                setBuyOrderId(newBuyOrderId);
            }
            if (!sellOrderId && sellOrderData.name) {
                const newSellOrderId = await runSetupStep("Sell order template", () => saveOrder("sell"));
                setSellOrderId(newSellOrderId);
            }

            const currentBuyOrderId = buyOrderId;
            const currentSellOrderId = sellOrderId;

            if (logicIsConfigured(buyAutomationData) && buyAutomationId) {
                await runSetupStep("Buy logic", () => persistAutomation("buy", currentBuyOrderId, monitorId, buyAutomationId));
            }
            if (logicIsConfigured(sellAutomationData) && sellAutomationId && sellAutomationId !== buyAutomationId) {
                await runSetupStep("Sell logic", () => persistAutomation("sell", currentSellOrderId, monitorId, sellAutomationId));
            }

            const nextBuyAutomationId = buyAutomationId || sellAutomationId;
            const nextSellAutomationId = sellAutomationId || buyAutomationId;

            await runSetupStep("Bot", () => saveStrategy(createdStrategyId, {
                name: strategyData.name,
                symbol: setupSymbol,
                monitorId,
                buyAutomationId: nextBuyAutomationId,
                sellAutomationId: nextSellAutomationId,
                isActive: strategyData.isActive !== false,
                shareMode: strategyData.shareMode,
                sharedEmails: strategyData.sharedEmails,
                sharedWith: getSharedWith(strategyData.shareMode, strategyData.sharedEmails),
            }));

            const successText = notifySetupSuccess({
                botName: strategyData.name,
                symbol: setupSymbol,
                isEdit: true,
            });
            setMessage(successText);
            await fetchExistingItems();
        } catch (err) {
            console.error(err);
            const text = err.setupNotified ? err.message : notifySetupError("Bot save", err);
            setError(text);
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (!setupSymbol) {
            setSymbolMeta({});
            setSymbolMonitors([]);
            setSymbolOrderTemplates([]);
            return;
        }

        const normalized = setupSymbol.trim().toUpperCase();
        if (normalized.startsWith("*")) {
            setSymbolMeta({ base: "*", quote: normalized.replace("*", "") });
        } else {
            getSymbol(normalized)
                .then((symbolObj) => setSymbolMeta(symbolObj || {}))
                .catch(() => setSymbolMeta({}));
        }

        getMonitorsBySymbol(normalized)
            .then((monitors) => setSymbolMonitors(Array.isArray(monitors) ? monitors : []))
            .catch(() => setSymbolMonitors([]));

        getAllOrderTemplates(normalized)
            .then((orders) => setSymbolOrderTemplates(Array.isArray(orders) ? orders : []))
            .catch(() => setSymbolOrderTemplates([]));
    }, [setupSymbol]);

    useEffect(() => {
        if (!setupSymbol) {
            setAutomationIndexes([]);
            return;
        }

        let cancelled = false;
        buildAutomationIndexesForSymbol({
            setupSymbol,
            symbolMeta,
            monitorData,
            createdMonitorId,
            symbolMonitors,
            market,
        })
            .then((indexes) => {
                if (!cancelled) setAutomationIndexes(indexes);
            })
            .catch(() => {
                if (!cancelled) setAutomationIndexes(getDefaultIndexes(setupSymbol));
            });

        return () => {
            cancelled = true;
        };
    }, [
        setupSymbol,
        symbolMeta,
        symbolMonitors,
        market,
        createdMonitorId,
        monitorData.name,
        monitorData.indexes,
        monitorData.interval,
        monitorData.type,
    ]);

    const monitorsForPair = filterMonitorsBySymbol(symbolMonitors, setupSymbol);

    const handleSelectOrder = (type, event) => {
        const id = event.target.value;
        const setId = type === "buy" ? setBuyOrderId : setSellOrderId;
        const setData = type === "buy" ? setBuyOrderData : setSellOrderData;

        if (!id) {
            setId(null);
            setData({
                ...DEFAULT_ORDER,
                side: type === "buy" ? "BUY" : "SELL",
                symbol: setupSymbol,
                name: "",
            });
            return;
        }

        const existing = symbolOrderTemplates.find((order) => String(order.id) === id);
        if (existing) {
            setData({ ...existing, leverage: existing.leverage || null });
            setId(existing.id);
        }
    };

    const isEditingBot = !!(createdStrategyId
        || new URLSearchParams(location.search).get("strategyId")
        || new URLSearchParams(location.search).get("botId"));

    return (
        <React.Fragment>
            <PrivatePageLayout className="unified-setup-page">
                        <PageHeader
                            eyebrow="Trading automation"
                            title="Algo Bot"
                            subtitle="Configure your market signal, orders, and buy/sell logic — then create one bot in a single step."
                            actions={isEditingBot ? (
                                <AppButtonLink to="/bots" variant="secondary" size="md">
                                    Back to bots
                                </AppButtonLink>
                            ) : null}
                        />

                        {message ? (
                            <div className="unified-setup-notice unified-setup-notice--success" role="status">
                                <Icon icon="mdi:check-circle-outline" className="unified-setup-notice-icon" />
                                <span>{message}</span>
                            </div>
                        ) : null}
                        {error ? (
                            <div className="unified-setup-notice unified-setup-notice--error" role="alert">
                                <Icon icon="mdi:alert-circle-outline" className="unified-setup-notice-icon" />
                                <span>{error}</span>
                            </div>
                        ) : null}

                        {showSuccess ? (
                            <div className="unified-setup-success-card">
                                <h2 className="unified-setup-success-title">Bot created successfully</h2>
                                <p className="unified-setup-success-text">
                                    {strategyData.name} is ready on {setupSymbol}. Manage it from the Bots page.
                                </p>
                                <div className="unified-setup-actions unified-setup-actions--start">
                                    <AppButtonLink to="/dashboard" variant="primary" size="md">
                                        Back to dashboard
                                    </AppButtonLink>
                                </div>
                            </div>
                        ) : null}

                        <SetupSection
                            icon="mdi:tune-variant"
                            title="Bot setup"
                            subtitle="Name your bot and choose the market."
                        >
                            <div className="row align-items-end">
                                <div className="col-md-5 col-12">
                                    <SetupField label="Trading pair">
                                        <SelectSymbol
                                            symbol={setupSymbol}
                                            onChange={handleTradingPairChange}
                                            onlyFavorites={false}
                                            fullWidth
                                        />
                                    </SetupField>
                                </div>
                                <div className="col-md-4 col-12">
                                    <SpotFuturesToggle
                                        showLabel
                                        value={market === "FUTURES" ? "futures" : "spot"}
                                        onChange={handleMarketChange}
                                    />
                                </div>
                                <div className="col-md-3 col-12">
                                    <SetupField label="Bot name" htmlFor="bot-name">
                                        <input
                                            id="bot-name"
                                            type="text"
                                            className="form-control"
                                            value={strategyData.name}
                                            onChange={(e) => updateStrategy("name", e.target.value)}
                                        />
                                    </SetupField>
                                </div>
                            </div>
                            <div className="unified-setup-options-grid">
                                <SetupField
                                    label="Max open positions"
                                    htmlFor="buyMaxOpenPositions"
                                    hint="0 = unlimited. Counts filled open positions (buys minus sells), not pending orders."
                                >
                                    <input
                                        id="buyMaxOpenPositions"
                                        type="number"
                                        min="0"
                                        step="1"
                                        className="form-control"
                                        value={buyMaxOpenPositions}
                                        onChange={(e) => setBuyMaxOpenPositions(Math.max(0, parseInt(e.target.value, 10) || 0))}
                                    />
                                </SetupField>
                                <SetupField
                                    label="Sharing"
                                    htmlFor="share-mode"
                                    hint={createdStrategyId ? "Public bots appear on the marketplace immediately after saving." : "Choose Public to list this bot on the marketplace after creation."}
                                >
                                    <select
                                        id="share-mode"
                                        className="form-select"
                                        value={strategyData.shareMode}
                                        onChange={handleShareModeChange}
                                    >
                                        <option value="private">Private</option>
                                        <option value="public">Public</option>
                                        <option value="shared">Shared</option>
                                    </select>
                                </SetupField>
                            </div>
                            {strategyData.shareMode === "shared" ? (
                                <SetupField label="Shared emails" htmlFor="shared-emails" hint="Comma-separated email addresses.">
                                    <input
                                        id="shared-emails"
                                        type="text"
                                        className="form-control"
                                        placeholder="name@company.com"
                                        value={strategyData.sharedEmails}
                                        onChange={handleSharedEmailsChange}
                                        onBlur={handleSharedEmailsBlur}
                                    />
                                </SetupField>
                            ) : null}
                            <SwitchInput
                                id="isActive"
                                text="Start bot when saved?"
                                isChecked={strategyData.isActive}
                                onChange={(event) => updateStrategy(event.target.id, event.target.value)}
                            />
                        </SetupSection>

                        <SetupSection
                            icon="mdi:chart-timeline-variant"
                            title="Market signal"
                            subtitle="What market condition should trigger your bot?"
                        >
                            <SetupField
                                label="Use existing monitor or create new"
                                hint={setupSymbol ? `Showing monitors for ${setupSymbol}.` : "Select a trading pair first."}
                            >
                                <select
                                    className="form-select"
                                    value={createdMonitorId || ""}
                                    onChange={handleSelectExistingMonitor}
                                    disabled={!setupSymbol}
                                >
                                    <option value="">Create new monitor</option>
                                    {monitorsForPair.map((monitor) => (
                                        <option key={monitor.id} value={monitor.id}>
                                            {formatMonitorOptionLabel(monitor)}
                                        </option>
                                    ))}
                                </select>
                            </SetupField>
                            {!createdMonitorId ? (
                                <div className="row">
                                    <div className="col-md-6 col-12">
                                        <SetupField label="Name" htmlFor="monitor-name">
                                            <input id="monitor-name" type="text" className="form-control" value={monitorData.name} onChange={(e) => updateMonitor("name", e.target.value)} />
                                        </SetupField>
                                    </div>
                                    <div className="col-md-3 col-12">
                                        <SetupField label="Type">
                                            <select className="form-select" value={monitorData.type} onChange={(e) => updateMonitor("type", e.target.value)}>
                                                <option value="CANDLES">Candles</option>
                                                <option value="CUSTOM">Custom</option>
                                            </select>
                                        </SetupField>
                                    </div>
                                    <div className="col-md-3 col-12">
                                        <SetupField label="Interval">
                                            <select className="form-select" value={monitorData.interval} onChange={(e) => updateMonitor("interval", e.target.value)}>
                                                <option value="1m">1m</option>
                                                <option value="3m">3m</option>
                                                <option value="5m">5m</option>
                                                <option value="15m">15m</option>
                                                <option value="30m">30m</option>
                                                <option value="1h">1h</option>
                                                <option value="2h">2h</option>
                                                <option value="4h">4h</option>
                                                <option value="6h">6h</option>
                                                <option value="8h">8h</option>
                                                <option value="12h">12h</option>
                                                <option value="1d">1d</option>
                                                <option value="3d">3d</option>
                                                <option value="1w">1w</option>
                                            </select>
                                        </SetupField>
                                    </div>
                                    <div className="col-md-12">
                                        <SetupField label="Technical index">
                                            <MonitorIndex indexes={monitorData.indexes} onChange={updateMonitor} />
                                        </SetupField>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <SwitchInput id="isActive" text="Is active?" isChecked={monitorData.isActive} onChange={updateMonitor} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <SwitchInput id="logs" text="Enable logs?" isChecked={monitorData.logs} onChange={updateMonitor} />
                                    </div>
                                </div>
                            ) : null}
                        </SetupSection>

                        <SetupSection
                            icon="mdi:file-document-edit-outline"
                            title="Order templates"
                            subtitle="Define how buy and sell orders are placed."
                        >
                            <div className="row">
                                <OrderTemplateColumn
                                    label="Buy order"
                                    side="buy"
                                    savedId={buyOrderId}
                                    orderData={buyOrderData}
                                    orderTemplates={symbolOrderTemplates}
                                    market={market}
                                    setupSymbol={setupSymbol}
                                    onSelectExisting={(event) => handleSelectOrder("buy", event)}
                                    onChange={makeOrderChangeHandler(setBuyOrderData)}
                                />
                                <OrderTemplateColumn
                                    label="Sell order"
                                    side="sell"
                                    savedId={sellOrderId}
                                    orderData={sellOrderData}
                                    orderTemplates={symbolOrderTemplates}
                                    market={market}
                                    setupSymbol={setupSymbol}
                                    onSelectExisting={(event) => handleSelectOrder("sell", event)}
                                    onChange={makeOrderChangeHandler(setSellOrderData)}
                                />
                            </div>
                        </SetupSection>

                        <SetupSection
                            icon="mdi:source-branch"
                            title="Buy & sell logic"
                            subtitle="Set conditions and actions independently for each side."
                        >
                            <div className="row">
                                <BotLogicColumn
                                    label="Buy logic"
                                    side="buy"
                                    logicData={buyAutomationData}
                                    setupSymbol={setupSymbol}
                                    market={market}
                                    automationIndexes={automationIndexes}
                                    onChange={makeAutomationChangeHandler(setBuyAutomationData)}
                                />
                                <BotLogicColumn
                                    label="Sell logic"
                                    side="sell"
                                    logicData={sellAutomationData}
                                    setupSymbol={setupSymbol}
                                    market={market}
                                    automationIndexes={automationIndexes}
                                    onChange={makeAutomationChangeHandler(setSellAutomationData)}
                                />
                            </div>
                        </SetupSection>

                        <div className="unified-setup-actions">
                            <AppButton
                                variant="primary"
                                size="md"
                                onClick={isEditingBot ? handleSaveBot : handleCreateBot}
                                disabled={isSaving}
                            >
                                {isSaving
                                    ? (isEditingBot ? "Saving…" : "Creating…")
                                    : (isEditingBot ? "Save bot" : "Create bot")}
                            </AppButton>
                        </div>
            </PrivatePageLayout>
        </React.Fragment>
    );
}

export default UnifiedSetup;